import { gradingIdSchema } from "@repo/app-types/entities/teammatePerformanceSummary";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { z } from "zod";

const configurationAssistantPrefillSchema = z.object({
  gradingId: gradingIdSchema,
  sentiment: z.enum(["positive", "negative"]),
  commentary: z.string(),
});

/** Prefill data for the configuration assistant */
export type ConfigurationAssistantPrefill = z.infer<
  typeof configurationAssistantPrefillSchema
>;

/** Parse the prefill query param from the URL once and clear it */
export function usePrefillFromUrl(): ConfigurationAssistantPrefill | null {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse the prefill query param once on mount using useState lazy initializer.
  // This ensures the value persists even after the URL is cleared.
  const [prefill] = useState<ConfigurationAssistantPrefill | null>(() => {
    const prefillParam = searchParams.get("prefill");
    if (!prefillParam) return null;

    try {
      return configurationAssistantPrefillSchema.parse(
        JSON.parse(prefillParam),
      );
    } catch {
      return null;
    }
  });

  // Clear the prefill param from the URL after mount to prevent re-sending on refresh.
  // Use a ref to ensure this only happens once.
  const hasClearedPrefillRef = useRef(false);
  useEffect(() => {
    if (!hasClearedPrefillRef.current && searchParams.has("prefill")) {
      hasClearedPrefillRef.current = true;
      setSearchParams(
        (prev) => {
          prev.delete("prefill");
          return prev;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  return prefill;
}

/** Build the message to send when auto-starting a conversation from prefill data */
export function buildPrefillMessage(
  prefill: ConfigurationAssistantPrefill,
): string {
  const sentimentLabel =
    prefill.sentiment === "positive" ? "positive" : "negative";

  return `I just reviewed a call and left ${sentimentLabel} feedback:
"${prefill.commentary}"

Here's the call I was reviewing: ${prefill.gradingId}

Can you help me update my agent based on this feedback?`;
}
